import type { FontFamily, TextStyle } from '@/types'

/**
 * On-screen fonts are chosen to be metric-compatible with the PDF Standard 14 fonts used on
 * export (Arial ≈ Helvetica, Times New Roman ≈ Times, Courier New ≈ Courier), so what you
 * type is what you get in the saved file.
 */
export const CSS_FONT: Record<FontFamily, string> = {
  sans: 'Helvetica, Arial, "Liberation Sans", "Nimbus Sans", sans-serif',
  serif: '"Times New Roman", Times, "Liberation Serif", "Nimbus Roman", serif',
  mono: '"Courier New", Courier, "Liberation Mono", "Nimbus Mono PS", monospace',
}

export const LINE_HEIGHT = 1.2
export const TEXT_PAD = 2

/** Distance from the top of a CSS line box (line-height 1.2) to the baseline, in em. */
export const BASELINE_EM: Record<FontFamily, number> = {
  sans: 0.9465,
  serif: 0.9375,
  mono: 0.8665,
}

export function cssFont(s: TextStyle) {
  return {
    fontFamily: CSS_FONT[s.fontFamily],
    fontSize: s.fontSize,
    fontWeight: s.bold ? 700 : 400,
    fontStyle: s.italic ? 'italic' : 'normal',
    lineHeight: LINE_HEIGHT,
  } as const
}

/** Map a pdf.js font descriptor to our nearest family/weight. */
export function classifyPdfFont(
  generic: string | undefined,
  name: string | undefined,
): Pick<TextStyle, 'fontFamily' | 'bold' | 'italic'> {
  const n = (name ?? '').toLowerCase()
  let fontFamily: FontFamily = 'sans'
  if (generic === 'serif' || /times|serif|georgia|garamond|cambria|book|roman|minion/.test(n))
    fontFamily = 'serif'
  if (/sans/.test(n)) fontFamily = 'sans'
  if (generic === 'monospace' || /courier|mono|consol/.test(n)) fontFamily = 'mono'
  return {
    fontFamily,
    bold: /bold|black|heavy|semibold|demi/.test(n),
    italic: /italic|oblique/.test(n),
  }
}
